import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { UserPlus, Pencil, Mail, Phone, MapPin, Tag, AlignLeft, X, Save, User as UserIcon } from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { LEAD_STATUS_VALUES, toValidStatus } from './leadStatuses';

// ── Design tokens (matches AddStaffForm / Leads / Members) ───────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types ─────────────────────────────────────────────────────────────────────
// NOTE: assumes POST /v1/leads (create) and PUT /v1/leads/:id (update) both
// return { success: boolean, data: <lead>, error?: string } — matches the
// shape already used by GET /v1/leads/:id and POST /v1/leads/:id/notes.
export interface SavedLead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  source: string | null;
  status: string | null;
  notes: string | null;
  city: string | null;
  interested_in?: string | null;
}

interface AddEditLeadModalProps {
  visible: boolean;
  onHide: () => void;
  onSaved: (lead: SavedLead) => void;
  leadId?: string | null; // omitted / null → "add" mode, provided → "edit" mode
}

interface LeadFormData {
  name: string;
  phone: string;
  email: string;
  source: string;
  status: string;
  city: string;
  interested_in: string;
  notes: string;
}

const EMPTY: LeadFormData = { name: '', phone: '', email: '', source: '', status: 'new', city: '', interested_in: '', notes: '' };

const STATUS_OPTIONS = LEAD_STATUS_VALUES;
const SOURCE_OPTIONS = ['facebook', 'instagram', 'google', 'referral', 'website', 'walk_in', 'whatsapp'];

// ── Small building blocks (matches AddStaffForm) ──────────────────────────────
const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span style={{ color: C.teal, fontWeight: 700, fontSize: '0.71rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
      {title}
    </span>
  </div>
);

const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: C.teal,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '5.5px',
};

const SELECT_ARROW = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5'%3E%3Cpath d='M0 0l4.5 5L9 0z' fill='rgba(17,85,80,0.45)'/%3E%3C/svg%3E")`;

const SelectField: React.FC<{
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}> = ({ label, icon, value, onChange, options, placeholder, disabled }) => (
  <div>
    <label style={FIELD_LABEL}>{label}</label>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '7.4px',
        height: '38px',
        padding: '0 11px',
        background: disabled ? '#f9fafb' : C.white,
        border: `1px solid ${G.line}`,
        borderRadius: '8.3px',
      }}
    >
      <span style={{ color: C.muted, display: 'flex', flexShrink: 0 }}>{icon}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          height: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: '0.79rem',
          color: value ? '#111827' : C.muted,
          cursor: disabled ? 'default' : 'pointer',
          appearance: 'none',
          backgroundImage: SELECT_ARROW,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0 center',
          paddingRight: '18px',
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace('_', ' ').replace(/^./, (c) => c.toUpperCase())}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const TextAreaField: React.FC<{
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ label, icon, value, onChange, placeholder, disabled }) => (
  <div>
    <label style={FIELD_LABEL}>{label}</label>
    <div
      style={{
        display: 'flex',
        gap: '7.4px',
        padding: '9px 11px',
        background: disabled ? '#f9fafb' : C.white,
        border: `1px solid ${G.line}`,
        borderRadius: '8.3px',
      }}
    >
      <span style={{ color: C.muted, display: 'flex', flexShrink: 0, marginTop: '2px' }}>{icon}</span>
      <InputTextarea
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{ flex: 1, border: 'none', outline: 'none', boxShadow: 'none', padding: 0, fontSize: '0.79rem', resize: 'vertical' }}
      />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const AddEditLeadModal: React.FC<AddEditLeadModalProps> = ({ visible, onHide, onSaved, leadId }) => {
  const toast = useRef<Toast>(null);
  const isEdit = !!leadId;

  const [formData, setFormData] = useState<LeadFormData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Rules: a worked lead can never go back to "new"; only admins can set "converted".
  const [originalStatus, setOriginalStatus] = useState('new');
  const [canConvert, setCanConvert] = useState(false);

  useEffect(() => {
    if (!visible) return;
    if (!isEdit) { setFormData(EMPTY); setOriginalStatus('new'); setCanConvert(false); return; }

    const fetchLead = async () => {
      try {
        setFetching(true);
        const res = await apiClient.get(`/v1/leads/${leadId}`);
        if (res.data.success) {
          const l = res.data.data;
          setOriginalStatus(toValidStatus(l.status));
          setCanConvert(!!res.data.viewer?.can_convert);
          setFormData({
            name: l.name || '',
            phone: l.phone || '',
            email: l.email || '',
            source: l.source || '',
            status: toValidStatus(l.status),
            city: l.city || '',
            interested_in: l.interested_in || '',
            notes: l.notes || '',
          });
        }
      } catch {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load lead details', life: 4000 });
      } finally {
        setFetching(false);
      }
    };
    fetchLead();
  }, [visible, isEdit, leadId]);

  const set = (key: keyof LeadFormData) => (v: string) => setFormData((p) => ({ ...p, [key]: v }));

  const statusOptions = STATUS_OPTIONS.filter((v) => {
    if (v === 'new') return originalStatus === 'new'; // no going back to New
    if (v === 'converted') return canConvert || originalStatus === 'converted'; // admin only
    return true;
  });

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => { onHide(); setIsClosing(false); }, 300);
  };

  const showError = (detail: string) =>
    toast.current?.show({ severity: 'error', summary: 'Validation Error', detail, life: 5000 });

  const handleSubmit = async () => {
    const name = formData.name.trim();
    if (!name) return showError('Name is required');
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      return showError('Enter a valid email address');
    }

    setLoading(true);
    try {
      const payload = {
        name,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        source: formData.source || null,
        status: formData.status || 'new',
        city: formData.city.trim() || null,
        interested_in: formData.interested_in.trim() || null,
        notes: formData.notes.trim() || null,
      };

      const res = isEdit
        ? await apiClient.put(`/v1/leads/${leadId}`, payload)
        : await apiClient.post('/v1/leads', payload);

      if (res.data.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: isEdit ? 'Lead updated successfully!' : 'Lead added successfully!',
          life: 3000,
        });
        const saved: SavedLead = res.data.data;
        setTimeout(() => { onSaved(saved); handleClose(); }, 250);
      } else {
        showError(res.data.error || 'Failed to save lead');
      }
    } catch (e: any) {
      showError(e.response?.data?.error || e.response?.data?.message || 'Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div style={{ ...iconBox(44), borderRadius: '12px', boxShadow: '0 6px 18px rgba(17,85,80,0.25)' }}>
        {isEdit ? <Pencil size={19} /> : <UserPlus size={20} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          {isEdit ? 'Edit Lead' : 'Add New Lead'}
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {isEdit ? "Update this lead's details" : 'Create a new lead record'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className={`lead-dialog ${isClosing ? 'lead-dialog-closing' : ''}`}
        maskClassName={`lead-mask ${isClosing ? 'lead-mask-closing' : ''}`}
        style={{ width: '90vw', maxWidth: '560px' }}
        modal
        dismissableMask={!loading}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .lead-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: leadMaskIn 0.3s ease; }
          .lead-mask-closing { animation: leadMaskOut 0.3s ease forwards !important; }
          .lead-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: leadDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
          .lead-dialog-closing.p-dialog { animation: leadDlgOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards !important; }
          .lead-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .lead-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .lead-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 72vh; overflow-y: auto; }
          @keyframes leadMaskIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes leadMaskOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes leadDlgIn  { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes leadDlgOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92) translateY(16px); } }
        `}</style>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            opacity: fetching ? 0.5 : 1,
            pointerEvents: fetching ? 'none' : 'auto',
            transition: 'opacity 0.2s',
          }}
        >
          {/* ── Contact ─────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<UserIcon size={14} />} title="Contact Details" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                <GlobalInputField
                  label="Name"
                  required
                  type="text"
                  icon={UserIcon}
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('name')(e.target.value)}
                  placeholder="Full name"
                  disabled={loading || fetching}
                  autoComplete="off"
                />
                <GlobalInputField
                  label="Phone"
                  type="tel"
                  icon={Phone}
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('phone')(e.target.value)}
                  placeholder="10-digit mobile number"
                  disabled={loading || fetching}
                  autoComplete="off"
                />
                <GlobalInputField
                  label="Email"
                  type="email"
                  icon={Mail}
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('email')(e.target.value)}
                  placeholder="name@example.com"
                  disabled={loading || fetching}
                  autoComplete="off"
                />
                <GlobalInputField
                  label="City"
                  type="text"
                  icon={MapPin}
                  value={formData.city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('city')(e.target.value)}
                  placeholder="City"
                  disabled={loading || fetching}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* ── Classification ──────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<Tag size={14} />} title="Classification" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                <GlobalInputField
                  label="Interested In"
                  type="text"
                  icon={Tag}
                  value={formData.interested_in}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('interested_in')(e.target.value)}
                  placeholder="Product / course / service"
                  disabled={loading || fetching}
                  autoComplete="off"
                />
                <SelectField
                  label="Status"
                  icon={<Tag size={14} />}
                  value={formData.status}
                  onChange={set('status')}
                  options={statusOptions}
                  placeholder="Select status"
                  disabled={loading || fetching}
                />
                <SelectField
                  label="Source"
                  icon={<Tag size={14} />}
                  value={formData.source}
                  onChange={set('source')}
                  options={SOURCE_OPTIONS}
                  placeholder="Select source"
                  disabled={loading || fetching}
                />
                <TextAreaField
                  label="Notes"
                  icon={<AlignLeft size={14} />}
                  value={formData.notes}
                  onChange={set('notes')}
                  placeholder="Any additional notes…"
                  disabled={loading || fetching}
                />
              </div>
            </div>
          </div>

          {/* ── Actions ─────────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
            <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleClose} disabled={loading} />
            <GlobalButton
              label={loading ? (isEdit ? 'SAVING...' : 'ADDING...') : isEdit ? 'SAVE CHANGES' : 'ADD LEAD'}
              variant="primary"
              icon={isEdit ? Save : UserPlus}
              onClick={handleSubmit}
              disabled={loading || fetching}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddEditLeadModal;